/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type {
  ButtonHTMLAttributes,
  CSSProperties,
  HTMLAttributes,
  ReactNode,
} from 'react';

import { CaretDownIcon as KaiShikiCaretDownIcon, DeleteIcon as KaiShikiDeleteIcon } from '@/Client/Icons/kaiShikiIconAliases';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { CloseIcon as KaiShikiCloseIcon, CommonActionButton } from '@kaiizen/kai-shiki/react';
import {
  MODAL_COLORS,
  MODAL_EFFECTS,
} from './ModalStyleLib';

const MODAL_CARD_STYLE = css`
  label: MODAL_CARD_STYLE;
  background: ${MODAL_COLORS.white};
  border-radius: 15px;
  padding: 30px 30px 20px;
  position: relative;
  box-shadow: ${MODAL_EFFECTS.cardShadow};
`;

const MODAL_CARD_DENSE_BOTTOM_STYLE = css`
  label: MODAL_CARD_DENSE_BOTTOM_STYLE;
  padding-bottom: 30px;
`;

const MODAL_CARD_FILL_HEIGHT_STYLE = css`
  label: MODAL_CARD_FILL_HEIGHT_STYLE;
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const CLOSE_BUTTON_STYLE = css`
  label: CLOSE_BUTTON_STYLE;
  position: absolute;
  /* 40x40 hit area; offset pulls back from 24 so the centered icon stays put */
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${MODAL_COLORS.text};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const TITLE_STYLE = css`
  label: TITLE_STYLE;
  font-family: CircularXX, sans-serif;
  font-size: ${pxToRem(24)};
  font-style: normal;
  font-weight: 700;
  line-height: 1;
  color: ${MODAL_COLORS.text};
`;

const SUBTITLE_STYLE = css`
  label: SUBTITLE_STYLE;
  margin-top: 16px;
  font-family: CircularXX, sans-serif;
  font-size: ${pxToRem(18)};
  font-style: normal;
  font-weight: 400;
  line-height: 1.333;
  letter-spacing: 0.18px;
  color: ${MODAL_COLORS.text};
`;

const BODY_STYLE = css`
  label: BODY_STYLE;
  margin-top: 18px;
  font-family: CircularXX, sans-serif;
  font-size: ${pxToRem(18)};
  font-style: normal;
  font-weight: 400;
  line-height: 1.333;
  letter-spacing: 0.18px;
  color: ${MODAL_COLORS.text};
  max-width: 450px;
`;

const DIVIDER_STYLE = css`
  label: DIVIDER_STYLE;
  margin-top: 14px;
  width: 100%;
  height: 1px;
  background: ${MODAL_COLORS.divider};
`;

const ACTIONS_STYLE = css`
  label: ACTIONS_STYLE;
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const BUTTON_BASE_STYLE: CSSProperties = {
  minHeight: 32,
  borderRadius: 5,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  fontFamily: 'CircularXX, sans-serif',
  fontSize: pxToRem(12),
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: 1.167,
  letterSpacing: '0.24px',
  background: MODAL_COLORS.white,
  cursor: 'pointer',
  padding: '0px 12px',
};

const CANCEL_BUTTON_STYLE: CSSProperties = {
  ...BUTTON_BASE_STYLE,
  minWidth: 83,
  border: `1px solid ${MODAL_COLORS.text}`,
  color: MODAL_COLORS.text,
  padding: '13px 16px',
};

const CANCEL_BUTTON_MUTED_STYLE: CSSProperties = {
  borderColor: MODAL_COLORS.textMuted,
  color: MODAL_COLORS.textMuted,
};

const SUCCESS_BUTTON_STYLE: CSSProperties = {
  ...BUTTON_BASE_STYLE,
  border: `1px solid ${MODAL_COLORS.green}`,
  color: MODAL_COLORS.green,
};

const DANGER_BUTTON_STYLE: CSSProperties = {
  ...BUTTON_BASE_STYLE,
  border: `1px solid ${MODAL_COLORS.dangerRed}`,
  color: MODAL_COLORS.dangerRed,
  gap: 8,
};

const CLOSE_ICON_STYLE = css`
  label: CLOSE_ICON_STYLE;
  flex: 0 0 auto;
`;

type ModalCardProps = {
  children: ReactNode;
  denseBottomPadding?: boolean;
  /** Fill the shell's height as a flex column (for fullScreen modals). */
  fillHeight?: boolean;
};

export const ModalCard = ({
  children,
  denseBottomPadding = false,
  fillHeight = false,
}: ModalCardProps) => (
  <div
    css={[
      MODAL_CARD_STYLE,
      denseBottomPadding && MODAL_CARD_DENSE_BOTTOM_STYLE,
      fillHeight && MODAL_CARD_FILL_HEIGHT_STYLE,
    ]}
  >
    {children}
  </div>
);

export const ModalCloseButton = ({
  children,
  ...buttonProps
}: ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button type='button' css={CLOSE_BUTTON_STYLE} {...buttonProps}>
    {children ?? <CloseIcon />}
  </button>
);

export const ModalTitle = (props: HTMLAttributes<HTMLDivElement>) => <div css={TITLE_STYLE} {...props} />;

export const ModalSubtitle = (props: HTMLAttributes<HTMLDivElement>) => <div css={SUBTITLE_STYLE} {...props} />;

export const ModalBody = (props: HTMLAttributes<HTMLDivElement>) => <div css={BODY_STYLE} {...props} />;

export const ModalDivider = () => <div css={DIVIDER_STYLE} />;

export const ModalActions = (props: HTMLAttributes<HTMLDivElement>) => <div css={ACTIONS_STYLE} {...props} />;

export const ModalCancelButton = ({
  muted = false,
  style,
  ...buttonProps
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  muted?: boolean;
}) => (
  <CommonActionButton
    type='button'
    style={{
      ...CANCEL_BUTTON_STYLE,
      ...(muted ? CANCEL_BUTTON_MUTED_STYLE : undefined),
      ...style,
    }}
    {...buttonProps}
  />
);

export const ModalSuccessButton = ({
  style,
  ...buttonProps
}: ButtonHTMLAttributes<HTMLButtonElement>) => (
  <CommonActionButton
    type='button'
    style={{
      ...SUCCESS_BUTTON_STYLE,
      ...style,
    }}
    {...buttonProps}
  />
);

export const ModalDangerButton = ({
  style,
  ...buttonProps
}: ButtonHTMLAttributes<HTMLButtonElement>) => (
  <CommonActionButton
    type='button'
    style={{
      ...DANGER_BUTTON_STYLE,
      ...style,
    }}
    {...buttonProps}
  />
);

export const SparkleIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
    <path
      d='M12 3V21'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M3 12H21'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M7.5 7.5L16.5 16.5'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M16.5 7.5L7.5 16.5'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

export const CloseIcon = () => (
  <span css={CLOSE_ICON_STYLE}>
    <KaiShikiCloseIcon size={20} />
  </span>
);

export const CaretDownIcon = () => (
  <span css={CLOSE_ICON_STYLE}>
    <KaiShikiCaretDownIcon color={MODAL_COLORS.iconMuted} size={12} />
  </span>
);

export const DeleteIcon = ({ size = 14 }: { size?: 14 | 16 }) => <KaiShikiDeleteIcon size={size} />;
